import { createContext, useContext, useEffect, useMemo, useState, useRef, useCallback } from "react";
import { messages, languageLabels } from "./messages";
import { authMessages } from "./authMessages";
import { siteMessages } from "./siteMessages";
import { getCompleteLocale, loadCompleteLocale } from "./completeLocale";
const LocaleContext = createContext(null);
const isSupportedLocale = locale => typeof locale === "string" && Object.prototype.hasOwnProperty.call(messages, locale);
const normalize = (source) => source.trim().replace(/\s+/g, " ");
const tables = Object.fromEntries(Object.keys(messages).map(locale => [locale,
  Object.fromEntries(Object.entries({ ...messages[locale], ...authMessages[locale], ...siteMessages[locale] }).map(([key, value]) => [normalize(key), value]))
]));
const normalizedLocales = new WeakMap();
function localeData(locale) {
  const dictionary = getCompleteLocale(locale);
  if (!dictionary) return { complete: {}, patterns: [] };
  if (normalizedLocales.has(dictionary)) return normalizedLocales.get(dictionary);
  const complete = Object.fromEntries(Object.entries(dictionary).map(([key, value]) => [normalize(key), value]));
const patterns = Object.entries(complete).filter(([key]) => /\{\w+\}/.test(key)).map(([key, values]) => {
  const names = [];
  const expression = key.split(/(\{\w+\})/).map(part => {
    if (/^\{\w+\}$/.test(part)) { names.push(part.slice(1, -1)); return "(.+?)"; }
    return part.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }).join("");
  return { expression: new RegExp(`^${expression}$`), names, values };
});
  const data = { complete, patterns };
  normalizedLocales.set(dictionary, data);
  return data;
}
export function translate(source, locale = "tr", params = {}) {
  if (typeof source !== "string") return source;
  const key = normalize(source);
  const { complete, patterns } = localeData(locale);
  let result = complete[key] ?? tables[locale]?.[key];
  if (result === undefined) {
    for (const pattern of patterns) {
      const match = key.match(pattern.expression);
      if (match) {
        result = pattern.values;
        params = { ...Object.fromEntries(pattern.names.map((name, index) => [name, match[index + 1]])), ...params };
        break;
      }
    }
  }
  if (result === undefined) return source;
  result = result.replace(/\{(\w+)\}/g, (token, name) => params[name] ?? token);
  return (source.match(/^\s*/)?.[0] || "") + result + (source.match(/\s*$/)?.[0] || "");
}
const statusCopy = {
  tr: ["Dil yükleniyor…", "Dil yüklenemedi. Tekrar dene.", "Tekrar dene"],
  en: ["Loading language…", "Language could not load. Please retry.", "Retry"],
  de: ["Sprache wird geladen…", "Sprache konnte nicht geladen werden. Bitte erneut versuchen.", "Erneut versuchen"],
  ar: ["جارٍ تحميل اللغة…", "تعذر تحميل اللغة. يرجى المحاولة مرة أخرى.", "إعادة المحاولة"],
  fr: ["Chargement de la langue…", "La langue n’a pas pu être chargée. Réessayez.", "Réessayer"],
  es: ["Cargando idioma…", "No se pudo cargar el idioma. Inténtalo de nuevo.", "Reintentar"],
  ru: ["Загрузка языка…", "Не удалось загрузить язык. Повторите попытку.", "Повторить"],
  zh: ["正在加载语言…", "无法加载语言，请重试。", "重试"],
};
function savedLocale() {
  try { const saved = localStorage.getItem("shule-locale"); return isSupportedLocale(saved) ? saved : "tr"; }
  catch { return "tr"; }
}
export function LocaleProvider({ children }) {
  const initial = useRef(null);
  if (initial.current === null) initial.current = savedLocale();
  const [locale, setLocale] = useState(initial.current);
  const [ready, setReady] = useState(() => Boolean(getCompleteLocale(initial.current)));
  const [pendingLocale, setPendingLocale] = useState(null);
  const [failedLocale, setFailedLocale] = useState(null);
  const sequence = useRef(0);
  const mounted = useRef(true);
  const requestLocale = useCallback(next => {
    if (!isSupportedLocale(next)) return;
    const request = ++sequence.current;
    setFailedLocale(null);
    const commit = () => {
      if (!mounted.current || request !== sequence.current) return;
      setLocale(next);
      setReady(true);
      setPendingLocale(null);
    };
    if (getCompleteLocale(next)) { commit(); return; }
    setPendingLocale(next);
    loadCompleteLocale(next).then(commit, () => {
      if (!mounted.current || request !== sequence.current) return;
      setPendingLocale(null);
      setFailedLocale(next);
    });
  }, []);
  useEffect(() => {
    mounted.current = true;
    requestLocale(initial.current);
    return () => { mounted.current = false; sequence.current += 1; };
  }, [requestLocale]);
  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = locale === "ar" ? "rtl" : "ltr";
    if (ready) { try { localStorage.setItem("shule-locale", locale); } catch {} }
  }, [locale, ready]);
  const value = useMemo(() => ({ locale, setLocale: requestLocale,
    t: (source, params) => translate(source, locale, params),
  }), [locale, requestLocale]);
  const copy = statusCopy[locale];
  return <LocaleContext.Provider value={value}>
    {ready ? children : <div className="container-shule py-12" aria-busy={Boolean(pendingLocale)} />}
    {pendingLocale && <p role="status" className="container-shule py-3">{copy[0]}</p>}
    {failedLocale && <div role="alert" className="container-shule py-3">
      <p>{copy[1]}</p>
      <button type="button" className="btn-outline-shule" onClick={() => requestLocale(failedLocale)}>{copy[2]}</button>
      {!ready && <button type="button" className="btn-outline-shule" onClick={() => requestLocale("tr")}>Türkçe</button>}
    </div>}
  </LocaleContext.Provider>;
}
export function useLocale() {
  const value = useContext(LocaleContext);
  if (!value) throw new Error("LocaleProvider is required");
  return value;
}
export function LanguagePicker() {
  const { locale, setLocale, t } = useLocale();
  return <label className="inline-flex items-center gap-2 text-xs">
    <span className="font-medium text-shule-mocha">{t("Dil")}</span>
    <select data-testid="language-picker" aria-label={t("Dil")} value={locale}
      onChange={(event) => setLocale(event.target.value)}
      className="min-h-[44px] max-w-[150px] rounded-full border border-shule-line bg-shule-paper px-3 text-xs text-shule-coffee focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-shule-coffee">
      {Object.entries(languageLabels).map(([code, name]) => <option key={code} value={code}>{name}</option>)}
    </select>
  </label>;
}
